import { Component, OnInit, Input } from '@angular/core';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { Observable, Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { Organization } from "../../../../common/models/organization.model";
import { EDiscoveryService } from "../../../services/eDiscovery.service";
import { eDiscoveryRequest, eDiscoveryRequestSearchSpec, eDiscoveryStatusSearchType } from "../../../models/eDiscovery.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { RouterLinkRendererComponent } from '../../../../common/components/ag-grid/router-link-renderer.component';
import { ModalInfoComponent } from "../../../../common/components/ag-grid/modal-info.component";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';

@Component({
    selector: 'eDiscovery-search-results',
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.scss'],
    providers: [GridFormatterService, EDiscoveryService],
    standalone: false
})
export class EDiscoverySearchResultsComponent implements OnInit {
    @Input() availableOrganizations: Organization[] = [];
    @Input() organization: Organization;
    @Input() searchRequest: eDiscoveryRequestSearchSpec;   

    completedData: Observable<eDiscoveryRequest[]>;
	completedGridOptions: GridOptions;
	completedGridApi?: GridApi;
    pendingData: Observable<eDiscoveryRequest[]>;
	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
    statusSearchTypes = eDiscoveryStatusSearchType;
    subscription: Subscription = new Subscription();
    

    constructor(private readonly _discoveryService: EDiscoveryService, private readonly _formatter: GridFormatterService
	) { }

    ngOnInit() {
        this.pendingGridOptions = {
            columnDefs: this.defineColumns("pending"),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.pendingGridApi = params.api;

            }, 
            suppressHorizontalScroll: false,
            pagination: true,
            paginationAutoPageSize: true,
            headerHeight: 50
        } as GridOptions;
        this.completedGridOptions = {
            columnDefs: this.defineColumns("completed"),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            onGridReady: (params) => {
				this.completedGridApi = params.api;

            },
            suppressHorizontalScroll: false,
            pagination: true,
            paginationAutoPageSize: true,
            headerHeight: 50
        } as GridOptions;

        const pendingSearch = {
            organizationId: this.searchRequest.organizationId, requestId: this.searchRequest.requestId, statusType: eDiscoveryStatusSearchType.Pending, useNames : true
        } as eDiscoveryRequestSearchSpec;
        const completedSearch = {
            organizationId: this.searchRequest.organizationId, requestId: this.searchRequest.requestId, statusType: eDiscoveryStatusSearchType.Completed, useNames: true
        } as eDiscoveryRequestSearchSpec;
        if (this.searchRequest.statusType !== eDiscoveryStatusSearchType.Completed) {
            this.pendingData = this._discoveryService.searchRequests(pendingSearch).pipe(tap((data) =>
            {                  
                data.forEach(d => {
                    if (!d.targetUrl) {
                        d.targetUrl = `/admin/eDiscovery/request/`
                    }
                });
            }));
        }
        if (this.searchRequest.statusType !== eDiscoveryStatusSearchType.Pending) {
            this.completedData = this._discoveryService.searchRequests(completedSearch).pipe(tap((data) => {
                data.forEach(d => {
                    if (!d.targetUrl) {
                        d.targetUrl = `/admin/eDiscovery/request/`
                    }
                });
            }));
        }        
    }

    onTabClicked(): void {

    }

    private defineColumns(gridType: string): ColDef[] {
        let colDef: ColDef[] = [];         
        const idColumn = {
            headerClass: "wrapped",
            headerName: "Request #",
            headerTooltip: "Request #",
            field: "requestId",
            tooltipField: "requestId",
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                idField: "requestId",
                textField: "requestId"
            },
            pinned: true,
            suppressMovable: true
        } as ColDef;
        const organizationColumn = {
            headerClass: "wrapped",
            headerName: "Organization",
            field: "organizationId",
            valueGetter: this.getOrganizationName(this),
            tooltipValueGetter: this.getOrganizationName(this),
            minWidth: 125, 
            maxWidth: 150
        } as ColDef;
        const contactsColumn = {
            headerClass: "wrapped",
            headerName: "Contacts",
            headerTooltip: "Contacts",
            field: "contacts",
            tooltipField: "contacts",
            cellRendererSelector: (params) => {
                return {
					component: ModalInfoComponent, params: {
                        text: (data: any) => { return data.contacts; }
                    }
                };
            },
            minWidth: 200,
            maxWidth: 225
        } as ColDef;
        const dateCompletedColumn = {
            headerClass: "wrapped",
            headerName: "Date Completed",
            headerTooltip: "Date Completed",
            field: "dateCompleted",
            valueGetter: this._formatter.formatDate("dateCompleted"),
            tooltipValueGetter: this._formatter.formatDate("dateCompleted"),
            comparator: dateComparator,
            filter: "agDateColumnFilter",
            filterParams: this._formatter.defaultDateFilterParams
        } as ColDef;
        const dateRequestedColumn = {
            headerClass: "wrapped",
            headerName: "Date Requested",
            headerTooltip: "Date Requested",
            field: "dateRequested",
            valueGetter: this._formatter.formatDate("dateRequested"),
            tooltipValueGetter: this._formatter.formatDate("dateRequested"),
            comparator: dateComparator,
            filter: "agDateColumnFilter",
            filterParams: this._formatter.defaultDateFilterParams
        } as ColDef;
        const applicationNumberColumn = {
            headerClass: "wrapped",
            headerName: "Application Numbers",
            headerTooltip: "Application Numbers",
            field: "applicationNumbers",
            tooltipField: "applicationNumbers",
            cellRendererSelector: (params) => {
                return {
					component: ModalInfoComponent, params: {
                        text: (data: any) => { return data.applicationNumbers; }
                    }
                };
            },
            minWidth: 200,
            maxWidth: 225
        } as ColDef;
        const requestedByColumn = {
            headerClass: "wrapped",
            headerName: "Requested By",
            headerTooltip: "Requested By",
            field: "requestedByName"
        } as ColDef;
        const workOrderColumn = {
            headerClass: "wrapped",
            headerName: "Work Orders",
            headerTooltip: "Work Orders",
            field: "workOrders",
            tooltipField: "workOrders",
            cellRendererSelector: (params) => {
                return {
					component: ModalInfoComponent, params: {
                        text: (data: any) => { return data.workOrders; }
                    }
                };
            },
            minWidth: 200,
            maxWidth: 225
        } as ColDef;
        const appDataIncludeColumn = {
            headerClass: "wrapped",
            headerName: "Application Data",
            headerTooltip: "Application Data",
            field: "includeApplicationData",
            valueGetter: this._formatter.formatBooleanToYesNo("includeApplicationData"),
            tooltipValueGetter: this._formatter.formatBooleanToYesNo("includeApplicationData")
        } as ColDef;
        const appNotesIncludeColumn = {
            headerClass: "wrapped",
            headerName: "Application Notes",
            headerTooltip: "Application Notes",
            field: "includeApplicationNotes",
            valueGetter: this._formatter.formatBooleanToYesNo("includeApplicationNotes"),
            tooltipValueGetter: this._formatter.formatBooleanToYesNo("includeApplicationNotes")
        } as ColDef;
        const allContactsIncludeColumn = {
            headerClass: "wrapped",
            headerName: "All Contacts",
            headerTooltip: "All Contacts",
            field: "includeNotesForAllContacts",
            valueGetter: this._formatter.formatBooleanToYesNo("includeNotesForAllContacts"),
            tooltipValueGetter: this._formatter.formatBooleanToYesNo("includeNotesForAllContacts")
        } as ColDef;
        const allWorkOrdersIncludeColumn = {
            headerClass: "wrapped",
            headerName: "All Work Orders",
            headerTooltip: "All Work Orders",
            field: "includeAllWorkOrders",
            valueGetter: this._formatter.formatBooleanToYesNo("includeAllWorkOrders"),
            tooltipValueGetter: this._formatter.formatBooleanToYesNo("includeAllWorkOrders")
        } as ColDef;
        const organizationNotesIncludeColumn = {
            headerClass: "wrapped",
            headerName: "Includes Organization Notes",
            headerTooltip: "Includes Organization Notes",
            field: "includeOrganizationNotes",
            valueGetter: this._formatter.formatBooleanToYesNo("includeOrganizationNotes"),
            tooltipValueGetter: this._formatter.formatBooleanToYesNo("includeOrganizationNotes"),
            minWidth: 150,
            maxWidth: 175
        } as ColDef;
        const notifyOnCompleteColumn = {
            headerClass: "wrapped",
            headerName: "Notify When Complete",
            headerTooltip: "Notify When Complete",
            field: "notifyWhenComplete",
            valueGetter: this._formatter.formatBooleanToYesNo("notifyWhenComplete"),
            tooltipValueGetter: this._formatter.formatBooleanToYesNo("notifyWhenComplete")
        } as ColDef;

        colDef.push(idColumn);
        colDef.push(organizationColumn);
        colDef.push(requestedByColumn);
        colDef.push(dateRequestedColumn);
        if (gridType === "pending") colDef.push(notifyOnCompleteColumn);
        if (gridType === "completed") colDef.push(dateCompletedColumn);
        colDef.push(organizationNotesIncludeColumn);
        colDef.push(applicationNumberColumn);
        colDef.push(appDataIncludeColumn);
        colDef.push(appNotesIncludeColumn);
        colDef.push(allContactsIncludeColumn);
        colDef.push(contactsColumn);
        colDef.push(allWorkOrdersIncludeColumn);
        colDef.push(workOrderColumn);
        this._formatter.applyFilterKeyboardHintTooltips(colDef);
        return colDef;
    }

    getOrganizationName(component: any): any {
        return (obj: any) => {
            if (obj.data && obj.data.organizationId) {
                let id = obj.data.organizationId;
                var org = this.availableOrganizations.find(o => o.id === id);
                if (org) {
                    return org.name;
                }                
            }
            return "Unknown Organization";
        };
    }
    
}

